/**
 * Composant d'icône réutilisable
 * Permet d'afficher des icônes SVG depuis le dossier assets/icons
 *
 * Usage:
 * <apidae-icon name="bold/network_it_programming/sidebar_minimalistic_empty"></apidae-icon>
 * ou pour les icônes à la racine:
 * <apidae-icon name="sidebar_minimalistic_empty"></apidae-icon>
 */

import { LitElement, html, unsafeCSS, CSSResultGroup } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement, property, state } from "lit/decorators.js";
import iconStyles from "../styles/icon.css?inline";

@customElement("apidae-icon")
export class Icon extends LitElement {
  static styles: CSSResultGroup = unsafeCSS(iconStyles);

  @property({ type: String })
  name: string = "";

  @property({ type: String })
  size: string = "24";

  @property({ type: String })
  color: string = "currentColor";

  @property({ type: String })
  class: string = "";

  @state()
  private iconSvg: string | null = null;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadIcon();
  }

  async updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("name")) {
      await this.loadIcon();
    }
  }

  private async loadIcon(): Promise<void> {
    if (!this.name) {
      this.iconSvg = null;
      return;
    }

    try {
      // Utiliser le chemin complet tel qu'il est fourni
      // Le nom doit être le chemin complet, ex: "bold/network_it_programming/sidebar_minimalistic_empty"
      const iconPath = `${this.name}.svg`;

      // Déterminer le chemin de base pour les assets
      let baseUrl = "";

      if (import.meta.env.DEV) {
        // En développement, Vite sert les fichiers depuis src/
        // Utiliser un chemin relatif depuis la racine du serveur
        baseUrl = `/src/assets/icons/${iconPath}`;
      } else {
        // En production, trouver le chemin du script chargé
        const scripts = Array.from(document.getElementsByTagName("script"));
        const widgetScript = scripts.find(
          (s) =>
            s.src &&
            (s.src.includes("apidae-widgets") || s.src.includes("widget")),
        );

        if (widgetScript && widgetScript.src) {
          // Extraire le répertoire du script
          const scriptUrl = new URL(widgetScript.src);
          const scriptPath = scriptUrl.pathname;
          const scriptDir = scriptPath.substring(
            0,
            scriptPath.lastIndexOf("/"),
          );
          baseUrl = `${scriptUrl.origin}${scriptDir}/assets/icons/${iconPath}`;
        } else {
          // Fallback: utiliser un chemin relatif depuis la racine
          baseUrl = `./assets/icons/${iconPath}`;
        }
      }

      const response = await fetch(baseUrl);
      if (!response.ok) {
        console.warn(
          `Icon ${this.name} not found at ${baseUrl} (status: ${response.status})`,
        );
        this.iconSvg = null;
        return;
      }

      const svgText = await response.text();
      this.iconSvg = svgText;
      this.requestUpdate();
    } catch (error) {
      console.warn(`Error loading icon ${this.name}:`, error);
      this.iconSvg = null;
    }
  }

  render() {
    if (!this.iconSvg) {
      return html``;
    }

    // Parser le SVG et remplacer les attributs de couleur
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(this.iconSvg, "image/svg+xml");
    const svgElement = svgDoc.querySelector("svg");

    if (!svgElement) {
      return html``;
    }

    // Mettre à jour la taille - utiliser la même valeur pour width et height pour un rendu carré
    svgElement.setAttribute("width", this.size);
    svgElement.setAttribute("height", this.size);
    svgElement.style.width = `${this.size}px`;
    svgElement.style.height = `${this.size}px`;

    // Remplacer les couleurs par currentColor pour permettre le style via CSS
    // Pour sidebar_minimalistic_empty, ne pas remplir le vide (garder fill="none")
    const isSidebarEmpty = this.name.includes("sidebar_minimalistic_empty");

    const paths = svgElement.querySelectorAll("path");
    paths.forEach((path) => {
      if (path.getAttribute("stroke")) {
        path.setAttribute("stroke", "currentColor");
      }
      if (isSidebarEmpty) {
        // Pour cette icône, forcer fill="none" et garder le stroke-width original
        path.setAttribute("fill", "none");
        // Ne pas modifier stroke-width pour cette icône car elle n'a pas de fill
      } else {
        // Pour les autres icônes, mettre stroke-width à 0 et utiliser fill
        path.setAttribute("stroke-width", "0");
        // Remplacer fill par currentColor si présent
        if (path.getAttribute("fill") && path.getAttribute("fill") !== "none") {
          path.setAttribute("fill", "currentColor");
        }
      }
    });

    return html`
      <div
        class="icon-container ${this.class}"
        style="color: ${this.color}; width: ${this.size}px; height: ${this
          .size}px;"
      >
        ${unsafeHTML(svgElement.outerHTML)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-icon": Icon;
  }
}
